import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useFormatPrice from "@/hooks/use-format-price";
import { cn } from "@/utils/cn";
import useGetProductVariants from "@babackapp/private-api-client/client/use-get-product-variants";
import { useSearchProducts } from "@babackapp/private-api-client/client/use-search-products";
import type { ProductVariantDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Button, ButtonLoading } from "@babackapp/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import { Input } from "@babackapp/ui/components/input";
import {
  AlertTriangle,
  ArrowLeft,
  Loader2,
  Search,
  ShoppingCart,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

type ExchangeVariantOverrideModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentVariantId: string;
  currentVariantPrice?: string;
  onConfirm: (variant: ProductVariantDto) => void | Promise<void>;
};

type SearchedProduct = {
  id: string;
  title: string;
  imageUrl?: string | null;
  status: string;
  sku?: string | null;
};

export function ExchangeVariantOverrideModal({
  open,
  onOpenChange,
  currentVariantId,
  currentVariantPrice,
  onConfirm,
}: ExchangeVariantOverrideModalProps) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const formatPrice = useFormatPrice();

  const [search, setSearch] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const debouncedSetQ = useDebouncedCallback(setDebouncedQ, 250);
  const [selectedProduct, setSelectedProduct] =
    useState<SearchedProduct | null>(null);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);

  // Reset internal state every open/close cycle.
  useEffect(() => {
    if (!open) {
      setSearch("");
      setDebouncedQ("");
      setSelectedProduct(null);
      setSelectedVariantId(null);
      setSubmitting(false);
    }
  }, [open]);

  const {
    data: searchData,
    isFetching: isSearching,
    error: searchFailure,
  } = useSearchProducts(shop.id, debouncedQ, {
    enabled: open && !selectedProduct && debouncedQ.trim().length > 0,
  });

  const products = useMemo<SearchedProduct[]>(
    () => (searchData as SearchedProduct[] | undefined) ?? [],
    [searchData],
  );

  const {
    data: variantsPayload,
    isFetching: isLoadingVariants,
    error: variantsFailure,
  } = useGetProductVariants({
    shopId: shop.id,
    productId: selectedProduct?.id ?? "",
    enabled: open && Boolean(selectedProduct?.id),
  });

  // The search now lists live Shopify products, but a merchant can still delete
  // one between the search and the click — and a return recorded before a delete
  // keeps pointing at the dead variant. Either way the endpoint answers 404, and
  // it must not read as "this product has no variants" (BBS-328).
  const isProductGone =
    (variantsFailure as { statusCode?: number } | null)?.statusCode === 404;

  const variants = variantsPayload?.variants ?? [];
  const selectedVariant = variants.find((v) => v.id === selectedVariantId);

  const currency = shop.preferences.currency;
  const priceDelta = (() => {
    if (!selectedVariant || currentVariantPrice === undefined) return null;
    const oldPrice = parseFloat(currentVariantPrice);
    if (isNaN(oldPrice)) return null;
    const delta = selectedVariant.price - oldPrice;
    if (delta === 0) return null;
    return {
      currentPrice: formatPrice({ amount: oldPrice, currency }),
      newPrice: formatPrice({ amount: selectedVariant.price, currency }),
      delta: `${delta > 0 ? "+" : ""}${formatPrice({ amount: delta, currency })}`,
    };
  })();

  async function handleConfirm() {
    if (!selectedVariant) return;
    try {
      setSubmitting(true);
      await onConfirm(selectedVariant as unknown as ProductVariantDto);
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t("returnDetail.exchangeVariantOverride.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <AlertTriangle className="size-4 shrink-0 mt-0.5 text-amber-600" />
          <span className="flex flex-col gap-1">
            <span>
              {t("returnDetail.exchangeVariantOverride.priceWarning")}
            </span>
            {priceDelta && (
              <span className="font-medium">
                {t("returnDetail.exchangeVariantOverride.priceDeltaDetail", {
                  currentPrice: priceDelta.currentPrice,
                  newPrice: priceDelta.newPrice,
                  delta: priceDelta.delta,
                })}
              </span>
            )}
          </span>
        </div>

        {!selectedProduct ? (
          <div className="flex flex-col gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  debouncedSetQ(e.target.value);
                }}
                placeholder={t(
                  "returnDetail.exchangeVariantOverride.searchByTitleOrSku",
                )}
                className="pl-9"
                autoFocus
              />
            </div>
            {(isSearching || debouncedQ.trim().length > 0) && (
              <div className="max-h-72 overflow-y-auto rounded-md border">
                {isSearching && (
                  <div className="flex items-center justify-center py-6 text-sm text-muted-foreground gap-2">
                    <Loader2 className="size-4 animate-spin" />
                    {t("misc.loading")}
                  </div>
                )}
                {!isSearching && searchFailure && (
                  <div className="py-6 text-center text-sm text-destructive">
                    {t("returnDetail.exchangeVariantOverride.searchError")}
                  </div>
                )}
                {!isSearching && !searchFailure && products.length === 0 && (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    {t("productSelect.noResult")}
                  </div>
                )}
                {!isSearching &&
                  !searchFailure &&
                  products.map((product) => (
                    <Button
                      key={product.id}
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        setSelectedProduct(product);
                        setSelectedVariantId(null);
                      }}
                      className="flex items-center gap-3 w-full h-auto justify-start px-3 py-2 rounded-none border-b last:border-b-0 [&_svg]:size-4"
                    >
                      {product.imageUrl ? (
                        <img
                          src={product.imageUrl}
                          alt=""
                          className="size-8 rounded object-cover shrink-0"
                        />
                      ) : (
                        <div className="size-8 rounded bg-muted flex items-center justify-center shrink-0">
                          <ShoppingCart className="text-muted-foreground" />
                        </div>
                      )}
                      <span className="flex min-w-0 flex-col items-start">
                        <span className="text-sm truncate font-normal">
                          {product.title}
                        </span>
                        {product.sku && (
                          <span className="text-xs text-muted-foreground font-normal truncate">
                            {product.sku}
                          </span>
                        )}
                      </span>
                      {product.status !== "ACTIVE" && (
                        <span className="ml-auto shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                          {product.status === "ARCHIVED"
                            ? t(
                                "returnDetail.exchangeVariantOverride.statusArchived",
                              )
                            : t(
                                "returnDetail.exchangeVariantOverride.statusDraft",
                              )}
                        </span>
                      )}
                    </Button>
                  ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedProduct(null);
                setSelectedVariantId(null);
              }}
              className="self-start text-muted-foreground"
            >
              <ArrowLeft />
              {t("returnDetail.exchangeVariantOverride.backToSearch")}
            </Button>

            <div className="flex items-center gap-2">
              {selectedProduct.imageUrl ? (
                <img
                  src={selectedProduct.imageUrl}
                  alt=""
                  className="size-10 rounded object-cover shrink-0"
                />
              ) : (
                <div className="size-10 rounded bg-muted flex items-center justify-center shrink-0">
                  <ShoppingCart className="size-4 text-muted-foreground" />
                </div>
              )}
              <span className="text-sm font-medium">
                {selectedProduct.title}
              </span>
            </div>

            <div className="max-h-72 overflow-y-auto rounded-md border">
              {isLoadingVariants && (
                <div className="flex items-center justify-center py-6 text-sm text-muted-foreground gap-2">
                  <Loader2 className="size-4 animate-spin" />
                  {t("misc.loading")}
                </div>
              )}
              {!isLoadingVariants && isProductGone && (
                <div className="py-6 px-3 text-center text-sm text-destructive">
                  {t("returnDetail.exchangeVariantOverride.productDeleted")}
                </div>
              )}
              {!isLoadingVariants && variantsFailure && !isProductGone && (
                <div className="py-6 px-3 text-center text-sm text-destructive">
                  {t("returnDetail.exchangeVariantOverride.searchError")}
                </div>
              )}
              {!isLoadingVariants &&
                !variantsFailure &&
                variants.length === 0 && (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    {t("returnDetail.exchangeVariantOverride.noVariants")}
                  </div>
                )}
              {!isLoadingVariants &&
                !variantsFailure &&
                variants.map((variant) => {
                  const isCurrent = variant.id === currentVariantId;
                  const isSelected = variant.id === selectedVariantId;
                  const outOfStock = variant.inventory_quantity <= 0;
                  return (
                    <Button
                      key={variant.id}
                      type="button"
                      variant="ghost"
                      onClick={() => setSelectedVariantId(variant.id)}
                      className={cn(
                        "flex items-center justify-between w-full h-auto px-3 py-2 rounded-none border-b last:border-b-0",
                        isSelected && "bg-accent",
                      )}
                    >
                      <span className="flex flex-col items-start">
                        <span className="text-sm font-normal">
                          {variant.title}
                          {isCurrent && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              (
                              {t(
                                "returnDetail.exchangeVariantOverride.current",
                              )}
                              )
                            </span>
                          )}
                        </span>
                        {variant.sku && (
                          <span className="text-xs text-muted-foreground font-normal">
                            {variant.sku}
                          </span>
                        )}
                      </span>
                      <span
                        className={cn(
                          "text-xs px-2 py-0.5 rounded-full",
                          outOfStock
                            ? "bg-destructive/10 text-destructive"
                            : "bg-green-100 text-green-700",
                        )}
                      >
                        {outOfStock
                          ? t("returnDetail.exchangeVariantOverride.outOfStock")
                          : t("returnDetail.exchangeVariantOverride.inStock", {
                              count: variant.inventory_quantity,
                            })}
                      </span>
                    </Button>
                  );
                })}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            {t("misc.cancel")}
          </Button>
          <ButtonLoading
            type="button"
            onClick={handleConfirm}
            disabled={!selectedVariant}
            isLoading={submitting}
          >
            {t("returnDetail.exchangeVariantOverride.confirm")}
          </ButtonLoading>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
